import { useMutation, useQueryClient } from '@tanstack/react-query'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { RouterProvider as AriaRouterProvider } from 'react-aria-components'
import { Outlet, useHref, useLocation, useNavigate } from 'react-router'
import { api, ApiError, onUnauthenticated } from './api/client'
import { keys, useBootstrap } from './api/queries'
import { NotificationsDrawer } from './components/notifications-drawer'
import { OpenMonthBanner } from './components/open-month-banner'
import { Skeleton } from './components/page'
import { Palette } from './components/palette'
import { ShortcutSheet } from './components/shortcut-sheet'
import { StepUpModal } from './components/step-up-modal'
import { toast, Toaster } from './components/toast'
import { Tree } from './components/tree'
import { chordTarget, inTextField } from './keys/shortcuts'
import { registerServiceWorker } from './push'
import { scopeFromPath } from './scope'
import { ShellProvider, useShell } from './store'

const NewWorkItemDialog = lazy(() => import('@/pages/app/new-work-item').then(m => ({ default: m.NewWorkItemDialog })))
const NewClientDialog = lazy(() => import('@/pages/app/new-client').then(m => ({ default: m.NewClientDialog })))

/**
 * The frame behind sign-in: tree + page area, bootstrap once, chords and global keys, forced
 * sign-out routing (PRD-018 §3.10), reconnect bar, palette, step-up modal, shortcut sheet, toasts.
 */
export function AppFrame() {
  const boot = useBootstrap()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => onUnauthenticated((err) => {
    if (err.reason === 'mfa_enrollment_required')
      navigate('/mfa/enroll', { replace: true })
    else if (err.reason === 'mfa_reenroll_required')
      navigate('/mfa/enroll', { replace: true, state: { reason: 'cfo_reset' } })
    else
      navigate('/signin', { replace: true, state: { from: location.pathname + location.search, reason: err.reason === 'no_session' ? 'session_ended' : err.reason } })
  }), [navigate, location.pathname, location.search])

  // PRD-029 §3.4: the push service worker is registered once the person is signed in, so an already-granted
  // permission keeps working after a redeploy; it caches nothing and intercepts no fetch.
  useEffect(() => {
    if (boot.isSuccess && 'serviceWorker' in navigator && 'Notification' in window && Notification.permission === 'granted')
      void registerServiceWorker()
  }, [boot.isSuccess])

  if (boot.isPending) {
    return (
      <div className="flex h-screen">
        <div className="w-[var(--shell-tree-width)] border-r border-secondary bg-secondary p-4"><Skeleton rows={8} /></div>
        <div className="flex-1 p-8"><Skeleton rows={4} /></div>
      </div>
    )
  }
  if (boot.isError) {
    if (boot.error instanceof ApiError && boot.error.status === 401)
      return null
    return (
      <div className="flex h-screen">
        <div className="w-[var(--shell-tree-width)] border-r border-secondary bg-secondary p-4"><Skeleton rows={8} /></div>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
          <p className="text-lg font-semibold text-primary">Could not load your workspace</p>
          <p className="text-sm text-tertiary">Check your connection, then try again. If it keeps failing, sign out and back in.</p>
          <button type="button" onClick={() => boot.refetch()} className="rounded-md border border-secondary px-3 py-1.5 text-sm">Retry</button>
        </div>
      </div>
    )
  }
  return (
    // React Aria links (Button href, Dropdown items) route through React Router instead of a full reload.
    <AriaRouterProvider navigate={(to, opts) => navigate(to, opts)} useHref={useHref}>
      <ShellProvider bootstrap={boot.data}>
        <Frame stale={boot.isRefetchError || (boot.failureCount > 0 && !boot.isFetching)} />
      </ShellProvider>
    </AriaRouterProvider>
  )
}

function PageFallback() {
  return <div className="p-8"><Skeleton rows={4} /></div>
}

function Frame({ stale }: { stale: boolean }) {
  const { bootstrap, setPaletteOpen, toggleTree, setSheetOpen, paletteOpen, stepUp, sheetOpen, newClientOpen, setNewClientOpen } = useShell()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const pendingGRef = useRef<number | null>(null)
  const [online, setOnline] = useState(true)
  const [details, setDetails] = useState<{ title: string, clusterId: string | null, fileId: string | null } | null>(null)

  const quick = useMutation({
    mutationFn: (title: string) => {
      const { clusterId, fileId } = scopeFromPath(location.pathname)
      const body: Record<string, string> = { title }
      if (fileId)
        body.fileId = fileId
      else if (clusterId)
        body.clusterId = clusterId
      return api<{ id: string, fileId: string | null, clusterId: string }>('/work-items/quick', { method: 'POST', json: body, idempotencyKey: crypto.randomUUID() })
    },
    onSuccess: (item) => {
      void qc.invalidateQueries({ queryKey: keys.bootstrap })
      void qc.invalidateQueries({ queryKey: ['work-items'] })
      navigate(`/w/${item.id}`)
    },
    onError: e => toast(e instanceof ApiError ? String(e.body.message ?? 'Could not create the task.') : 'Could not create the task. Check your connection and try again.', { tone: 'error' }),
  })

  // Global keys and `g` chords (PRD-018 §3.11). Suppressed in text fields and while an overlay is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(!paletteOpen)
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault()
        toggleTree()
        return
      }
      if (inTextField(e.target) || paletteOpen || stepUp || sheetOpen || details || newClientOpen || e.metaKey || e.ctrlKey || e.altKey)
        return
      if (pendingGRef.current !== null) {
        window.clearTimeout(pendingGRef.current)
        pendingGRef.current = null
        const to = chordTarget(e.key)
        if (to === '/my-work/notifications' && location.pathname !== '/my-work/notifications') {
          e.preventDefault()
          const next = new URLSearchParams(location.search)
          next.set('notifications', '1')
          navigate({ pathname: location.pathname, search: `?${next.toString()}` })
        }
        else if (to) {
          e.preventDefault()
          navigate(to)
        }
        return
      }
      if (e.key === 'g') {
        pendingGRef.current = window.setTimeout(() => {
          pendingGRef.current = null
        }, 800)
        return
      }
      if (e.key === '/') {
        e.preventDefault()
        setPaletteOpen(true)
      }
      else if (e.key === '?') {
        e.preventDefault()
        setSheetOpen(true)
      }
      else if (e.key === 'c') {
        e.preventDefault()
        setPaletteOpen(true, '')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate, location.pathname, location.search, paletteOpen, setPaletteOpen, toggleTree, setSheetOpen, stepUp, sheetOpen, details, newClientOpen])

  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  return (
    <div className="flex h-screen overflow-hidden bg-primary" data-density={bootstrap.me.settings.density}>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-primary focus:px-3 focus:py-1">Skip to content</a>
      <Tree />
      <div className="flex min-w-0 flex-1 flex-col">
        {!online || stale ? <div role="status" className="bg-warning-secondary px-6 py-1 text-center text-xs text-warning-primary" data-testid="reconnect-bar">Reconnecting… badges may be stale</div> : null}
        <OpenMonthBanner />
        <main id="main" className="flex min-h-0 flex-1 flex-col overflow-auto">
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <Palette
        onNewTask={title => quick.mutate(title)}
        onNewTaskDetails={(title) => {
          const scope = scopeFromPath(location.pathname)
          setDetails({ title, ...scope })
        }}
      />
      {details
        ? (
            <Suspense fallback={null}>
              <NewWorkItemDialog clusterId={details.clusterId} fileId={details.fileId} initialKind="task" initialTitle={details.title} onClose={() => setDetails(null)} />
            </Suspense>
          )
        : null}
      {newClientOpen
        ? (
            <Suspense fallback={null}>
              <NewClientDialog onClose={() => setNewClientOpen(false)} />
            </Suspense>
          )
        : null}
      <NotificationsDrawer />
      <StepUpModal />
      <ShortcutSheet />
      <Toaster />
    </div>
  )
}
